/**
 * =============================================================================
 * Hook: useAffiliateMaterials
 * Versão: 5.0.0 (Fase 4 — deduplicação por material e agregação de programas)
 *
 * Descrição:
 *   Lista materiais de divulgação ativos para TODOS os programas em que o
 *   afiliado está inscrito (memberships com status='active').
 *   - A mesma linha pode retornar para cada programa vinculado; aqui
 *     agrupamos por material, acumulando os programas em uma única lista.
 *   - RLS no banco continua protegendo a leitura por tenant.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { useAffiliateMyMemberships } from '@/hooks/useAffiliateMyMemberships';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateMaterials');

export interface AffiliateMaterialProgram {
  id: string | null;
  name: string;
}

export interface AffiliateMaterialRow {
  id: string;
  title: string;
  description: string | null;
  material_type: string; // 'image' | 'video' | 'copy' | 'banner' | etc.
  file_url: string | null;
  storage_path: string | null;
  text_content: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  download_count: number;
  sort_order: number;
  campaign_id: string | null;
  created_at: string;
  // v5.0.0 — deduplicado por material; `program_id`/`program_name` mantêm o
  // primeiro registro para compatibilidade, enquanto `programs` traz todos.
  program_id: string | null;
  program_name: string;
  programs: AffiliateMaterialProgram[];
}

export function useAffiliateMaterials() {
  const { affiliate, ready } = useAffiliateAuth();
  const { data: memberships, isLoading: membershipsLoading } = useAffiliateMyMemberships();

  const activePrograms = (memberships ?? []).filter((m) => m.status === 'active');

  return useQuery({
    queryKey: [
      'affiliate-materials',
      affiliate?.id,
      activePrograms.map((p) => p.program_id).sort().join(','),
    ],
    enabled: ready && !!affiliate?.id && !membershipsLoading,
    queryFn: async (): Promise<AffiliateMaterialRow[]> => {
      if (activePrograms.length === 0) return [];

      // v5.0.0 — leitura 100% server-side via RPC SECURITY DEFINER.
      // As leituras diretas anteriores dependiam de policies que consultavam
      // `affiliates` (tabela fechada para o próprio afiliado), retornando vazio.
      const { data, error } = await supabase.rpc('list_my_affiliate_materials', {
        p_affiliate_id: affiliate?.id ?? null,
      } as any);

      if (error) {
        log.error('Falha ao listar materiais do afiliado (RPC)', error);
        throw error;
      }

      // v5.0.0 — deduplicar por material, agregando os programas.
      const map = new Map<string, AffiliateMaterialRow>();
      const rawRows = ((data as unknown as any[]) ?? []).map((m) => ({
        ...m,
        download_count: Number(m.download_count ?? 0),
        sort_order: Number(m.sort_order ?? 0),
        program_name: m.program_name ?? 'Programa',
      })) as AffiliateMaterialRow[];

      rawRows.forEach((row) => {
        const existing = map.get(row.id);
        const program = { id: row.program_id, name: row.program_name };
        if (!existing) {
          map.set(row.id, { ...row, programs: [program] });
        } else {
          const alreadyAdded = existing.programs.some((p) => p.id === program.id);
          if (!alreadyAdded) {
            existing.programs.push(program);
          }
        }
      });

      const rows = Array.from(map.values()).map((row) => ({
        ...row,
        // v5.0.0 — garantir program_id/program_name consistentes com o primeiro programa.
        program_id: row.programs[0]?.id ?? row.program_id,
        program_name: row.programs[0]?.name ?? row.program_name,
      }));

      rows.sort((a, b) => {
        const orderDiff = a.sort_order - b.sort_order;
        if (orderDiff !== 0) return orderDiff;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      return rows;
    },

    staleTime: 60_000,
  });
}
